import { Link } from '@tanstack/react-router';
import {
  CheckCheck,
  CloudOff,
  CloudUpload,
  HardDrive,
  LoaderCircle,
  Network,
  Router,
  ServerCrash,
  ShieldAlert,
  TriangleAlert,
  Wifi,
} from 'lucide-react';

import { cx } from '../components/ui.tsx';
import { useLang } from '../i18n.ts';
import { clientLink, type ClientLink } from '../pages/hub/clientModel.ts';
import { desktop, useDesktopClient, useDesktopHub, useDesktopSync } from '../platform.ts';
import { useConnection } from './connection.ts';

/**
 * Sync status on every screen (CLAUDE.md §3.3.5): internet and server, and in Dob Desktop also
 * this PC's operations not sent yet. Never color alone: each state has its own icon and words.
 */
export function ConnectionBadge({ className }: { readonly className?: string }) {
  const { t } = useLang();
  const { state } = useConnection();
  const sync = useDesktopSync();
  const hub = useDesktopHub();
  const client = useDesktopClient();
  const styles = {
    // Before the first answer: neither "online" nor a fault (owner, 2026-10-07).
    checking: {
      icon: LoaderCircle,
      text: t('connection.checking'),
      cls: 'text-muted border-border bg-surface-2 border-dotted',
    },
    online: {
      icon: Wifi,
      text: t('connection.online'),
      cls: 'text-success border-success-border bg-success-bg',
    },
    offline: {
      icon: CloudOff,
      text: t('connection.offline'),
      cls: 'text-warning border-warning-border bg-warning-bg border-2',
    },
    serverDown: {
      icon: ServerCrash,
      text: t('connection.serverDown'),
      cls: 'text-error border-error-border bg-error-bg border-2 border-dashed',
    },
  }[state];
  const Icon = styles.icon;
  const title = desktop
    ? sync === null
      ? t('connection.desktopNotRegistered')
      : undefined
    : t('connection.onlineOnly');
  return (
    <span
      role="status"
      title={title}
      className={cx('items-center gap-1.5', className ?? 'inline-flex')}
    >
      <span
        className={cx(
          'inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-xs font-semibold',
          styles.cls,
        )}
      >
        <Icon className="size-3.5" aria-hidden />
        {styles.text}
      </span>
      {sync ? <PendingPill pending={sync.pending} rejected={sync.rejected} /> : null}
      {hub ? (
        // The hub PC (M7-3): its status page, from every screen and before sign-in.
        <Link
          to="/hub"
          className="inline-flex items-center gap-1.5 rounded-full border border-border px-2.5 py-1 text-xs font-semibold hover:bg-surface-2"
        >
          <Router className="size-3.5" aria-hidden />
          {t('hub.link')}
        </Link>
      ) : null}
      {client ? <ClientLinkPill link={clientLink(client)} /> : null}
    </span>
  );
}

function PendingPill({
  pending,
  rejected,
}: {
  readonly pending: number;
  readonly rejected: number;
}) {
  const { t } = useLang();
  const pill =
    'inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-xs font-semibold';
  if (rejected > 0) {
    return (
      <span
        className={cx(pill, 'text-error border-error-border bg-error-bg border-2 border-dashed')}
      >
        <TriangleAlert className="size-3.5" aria-hidden />
        {t('connection.rejected', { count: rejected })}
      </span>
    );
  }
  if (pending > 0) {
    return (
      <span className={cx(pill, 'text-warning border-warning-border bg-warning-bg border-2')}>
        <CloudUpload className="size-3.5" aria-hidden />
        {t('connection.pending', { count: pending })}
      </span>
    );
  }
  return (
    <span className={cx(pill, 'text-success border-success-border bg-success-bg')}>
      <CheckCheck className="size-3.5" aria-hidden />
      {t('connection.allSent')}
    </span>
  );
}

/**
 * An ordinary PC (M7-4): which way its operations go now, linking to the Connection page. "Not
 * your hub" has its own icon, words and dashed border (never colour alone).
 */
function ClientLinkPill({ link }: { readonly link: ClientLink }) {
  const { t } = useLang();
  const pill =
    'inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-xs font-semibold hover:bg-surface-2';
  const look = {
    starting: { icon: Network, key: 'client.stateSearching', cls: 'border-border' },
    hub: { icon: Router, key: 'client.linkHub', cls: 'border-border' },
    server: { icon: Wifi, key: 'client.linkServer', cls: 'border-border' },
    alone: {
      icon: HardDrive,
      key: 'client.linkAlone',
      cls: 'text-warning border-warning-border bg-warning-bg border-2',
    },
    not_your_hub: {
      icon: ShieldAlert,
      key: 'client.linkNotYourHub',
      cls: 'text-error border-error-border bg-error-bg border-2 border-dashed',
    },
  } as const;
  const { icon: Icon, key, cls } = look[link];
  return (
    <Link to="/connection" className={cx(pill, cls)}>
      <Icon className="size-3.5" aria-hidden />
      {t(key)}
    </Link>
  );
}
